import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, AlertCircle, Clock, Server, User, RefreshCw, Globe, Tag, FolderTree, Hash, ExternalLink, Loader2, CheckCircle2, Sparkles, BookOpen, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_LABELS = { 0: 'N/C', 1: 'Info', 2: 'Warning', 3: 'Average', 4: 'High', 5: 'Disaster' };
const SEVERITY_STYLES = {
  0: 'bg-slate-500 text-white border-slate-500',
  1: 'bg-blue-500 text-white border-blue-500',
  2: 'bg-yellow-500 text-white border-yellow-500',
  3: 'bg-orange-500 text-white border-orange-500',
  4: 'bg-red-500 text-white border-red-500',
  5: 'bg-red-700 text-white border-red-700',
};

const COLUMNS = [
  { id: 'new', title: 'Novo / Ativos', color: 'border-t-red-500', countColor: 'bg-red-100 text-red-700' },
  { id: 'ack', title: 'Identificado (ACK)', color: 'border-t-amber-500', countColor: 'bg-amber-100 text-amber-700' },
  { id: 'investigating', title: 'Em Atendimento', color: 'border-t-blue-500', countColor: 'bg-blue-100 text-blue-700' },
  { id: 'pending', title: 'Pendente', color: 'border-t-purple-500', countColor: 'bg-purple-100 text-purple-700' },
  { id: 'resolved', title: 'Resolvidos', color: 'border-t-emerald-500', countColor: 'bg-emerald-100 text-emerald-700' },
];

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0m';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function IncidentCard({ incident, index, onClick }) {
  const sev = incident.severity || 0;
  const now = Math.floor(Date.now() / 1000);
  const elapsed = incident.clock ? now - incident.clock : 0;

  return (
    <Draggable draggableId={incident.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`mb-2 transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}
          data-testid={`incident-card-${incident.id}`}
        >
          <Card
            className="cursor-pointer hover:shadow-md transition-all border"
            onClick={() => onClick(incident)}
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className={`text-xs px-2 py-0.5 ${SEVERITY_STYLES[sev]}`}>
                    {SEVERITY_LABELS[sev]}
                  </Badge>
                  {incident.acknowledged && (
                    <Badge variant="outline" className="text-xs px-2 py-0.5 bg-emerald-500 text-white border-emerald-500">
                      <CheckCircle2 className="h-3 w-3 mr-0.5" />ACK
                    </Badge>
                  )}
                </div>
                <span className="text-xs font-mono text-muted-foreground">INC-{incident.id.slice(-6)}</span>
              </div>
              <p className="text-sm font-semibold line-clamp-1 mb-1">{incident.name}</p>
              <p className="text-xs text-muted-foreground line-clamp-1 mb-3">
                <Server className="inline h-3 w-3 mr-1" />{incident.host_name}
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />{formatDuration(elapsed)}
                </span>
                {incident.acknowledged && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
  );
}

function KanbanColumn({ column, incidents, onCardClick }) {
  return (
    <div className="flex flex-col min-w-[260px] w-full" data-testid={`column-${column.id}`}>
      <div className={`rounded-t-lg border-t-4 ${column.color} bg-muted/30 px-3 py-2 flex items-center justify-between`}>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">{column.title}</h3>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${column.countColor}`}>
          {incidents.length}
        </span>
      </div>
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-2 rounded-b-lg border border-t-0 min-h-[200px] transition-colors ${
              snapshot.isDraggingOver ? 'bg-primary/5' : 'bg-background'
            }`}
          >
            <ScrollArea className="h-[calc(100vh-240px)]">
              {incidents.map((inc, index) => (
                <IncidentCard key={inc.id} incident={inc} index={index} onClick={onCardClick} />
              ))}
              {incidents.length === 0 && (
                <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
                  Nenhum incidente
                </div>
              )}
              {provided.placeholder}
            </ScrollArea>
          </div>
        )}
      </Droppable>
    </div>
  );
}

function IncidentDetailModal({ incident, open, onClose, authHeader }) {
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savingSolution, setSavingSolution] = useState(false);
  const navigate = useNavigate();
  const incidentId = incident?.id;

  useEffect(() => {
    if (!incidentId || !open) { setDetails(null); setAiResult(null); return; }
    setDetailsLoading(true);
    axios.get(`${API}/incident-board/${incidentId}/details`, { headers: authHeader })
      .then(res => setDetails(res.data))
      .catch(() => setDetails(null))
      .finally(() => setDetailsLoading(false));
  }, [incidentId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!incident) return null;
  const sev = incident.severity || 0;

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />}
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-sm font-medium">{value || '-'}</p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`${SEVERITY_STYLES[sev]}`}>{SEVERITY_LABELS[sev]}</Badge>
              <span className="text-xs font-mono text-muted-foreground">INC-{incident.id.slice(-6)}</span>
            </div>
            {details?.trigger?.occurrence_count > 0 && (
              <Badge variant="secondary" className="text-xs">
                <Hash className="h-3 w-3 mr-1" />{details.trigger.occurrence_count}x em 90d
              </Badge>
            )}
          </div>
          <DialogTitle className="text-lg leading-tight">{incident.name}</DialogTitle>
        </DialogHeader>

        {detailsLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <Tabs defaultValue="details" className="flex-1 min-h-0">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="details">Detalhes</TabsTrigger>
              <TabsTrigger value="host-history">Histórico do Host</TabsTrigger>
              <TabsTrigger value="trigger-history">Histórico da Trigger</TabsTrigger>
              <TabsTrigger value="annotations">Anotações</TabsTrigger>
              <TabsTrigger value="ai-analysis" className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />Análise com IA</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-3">
              <ScrollArea className="h-[calc(85vh-220px)]">
                <div className="space-y-5 pr-3">
                  {/* Alert Info */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Alerta</p>
                    <div className="grid grid-cols-2 gap-3 bg-muted/30 rounded-lg p-3">
                      <InfoRow label="Início" value={details?.event?.timestamp ? formatDate(details.event.timestamp) : formatDate(incident.timestamp)} icon={Clock} />
                      <InfoRow label="Duração" value={details?.event?.duration || incident.duration} icon={Clock} />
                      <InfoRow label="Acknowledged" value={details?.event?.acknowledged ? 'Sim' : (incident.acknowledged ? 'Sim' : 'Não')} />
                      <InfoRow label="Ocorrências (90d)" value={details?.trigger?.occurrence_count || '-'} icon={Hash} />
                      {(details?.event?.opdata || incident.opdata) && (
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Dados Operacionais</p>
                          <p className="text-xs font-mono bg-muted p-2 rounded mt-1">{details?.event?.opdata || incident.opdata}</p>
                        </div>
                      )}
                      {details?.trigger?.comments && (
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Comentários da Trigger</p>
                          <p className="text-sm bg-muted p-2 rounded mt-1">{details.trigger.comments}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Host Info */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Host</p>
                    <div className="grid grid-cols-2 gap-3 bg-muted/30 rounded-lg p-3">
                      <InfoRow label="Nome" value={details?.host?.name || incident.host_name} icon={Server} />
                      <InfoRow label="IP" value={details?.host?.ip} icon={Globe} />
                      <InfoRow label="Categoria" value={details?.host?.category} icon={Tag} />
                      <InfoRow label="Hostname" value={details?.host?.host} />
                      {details?.host?.groups?.length > 0 && (
                        <div className="col-span-2 flex items-start gap-2">
                          <FolderTree className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Grupos</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {details.host.groups.map((g, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{g}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {details?.host?.tags?.length > 0 && (
                        <div className="col-span-2 flex items-start gap-2">
                          <Tag className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Tags</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {details.host.tags.map((t, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{t.tag}: {t.value}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Link to host detail */}
                  {details?.host?.id && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => { onClose(); navigate(`/dashboard/hosts/${details.host.id}`); }}
                      data-testid="view-host-details-btn"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Exibir Detalhes Completos do Host
                    </Button>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="host-history" className="mt-3">
              <ScrollArea className="h-[calc(85vh-220px)]">
                <div className="space-y-1.5 pr-3">
                  {details?.host_history?.length > 0 ? details.host_history.map((h, i) => (
                    <div key={h.id || i} className="flex items-center gap-3 p-2 rounded-lg border bg-card text-sm">
                      <Badge variant="outline" className={`text-xs shrink-0 ${SEVERITY_STYLES[h.severity]}`}>
                        {h.severity_label}
                      </Badge>
                      <span className="flex-1 truncate">{h.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{formatDate(h.timestamp)}</span>
                      {h.resolved ? (
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">Resolvido</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 shrink-0">Ativo</Badge>
                      )}
                    </div>
                  )) : (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      Nenhum histórico disponível
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="trigger-history" className="mt-3">
              <ScrollArea className="h-[calc(85vh-220px)]">
                <div className="space-y-1.5 pr-3">
                  {details?.trigger_history?.length > 0 ? details.trigger_history.map((h, i) => (
                    <div key={h.id || i} className="flex items-center gap-3 p-2 rounded-lg border bg-card text-sm">
                      <Badge variant="outline" className={`text-xs shrink-0 ${SEVERITY_STYLES[h.severity]}`}>
                        {h.severity_label}
                      </Badge>
                      <span className="flex-1 truncate">{h.host_name || h.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{formatDate(h.timestamp)}</span>
                      {h.resolved ? (
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">Resolvido</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 shrink-0">Ativo</Badge>
                      )}
                    </div>
                  )) : (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      Nenhum histórico disponível
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="annotations" className="mt-3">
              <ScrollArea className="h-[calc(85vh-220px)]">
                <div className="space-y-2 pr-3">
                  {details?.acknowledges?.length > 0 ? details.acknowledges.map((ack, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium">{ack.user}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{ack.time}</span>
                      </div>
                      {ack.message && (
                        <p className="text-sm text-foreground bg-muted/50 rounded p-2 mt-1">{ack.message}</p>
                      )}
                      {ack.action && (
                        <p className="text-xs text-muted-foreground mt-1">{ack.action}</p>
                      )}
                    </div>
                  )) : (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      Nenhuma anotação disponível
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="ai-analysis" className="mt-3">
              <ScrollArea className="h-[calc(85vh-220px)]">
                <div className="space-y-4 pr-3">
                  {!aiResult && !aiLoading && (
                    <div className="text-center py-8">
                      <Sparkles className="h-10 w-10 mx-auto mb-3 text-purple-400 opacity-50" />
                      <p className="text-sm text-muted-foreground mb-4">A IA vai analisar o incidente, histórico do host, trigger e propor uma solução</p>
                      <Button onClick={async () => {
                        setAiLoading(true);
                        try {
                          const res = await axios.post(`${API}/aiops/analysis`, { type: 'rca', incident_id: incidentId }, { headers: authHeader });
                          setAiResult(res.data.response);
                        } catch (err) {
                          setAiResult(`Erro: ${err.response?.data?.detail || 'Falha na análise'}`);
                        } finally { setAiLoading(false); }
                      }} className="gap-2 bg-purple-600 hover:bg-purple-700">
                        <Sparkles className="h-4 w-4" />Iniciar Análise com IA
                      </Button>
                    </div>
                  )}

                  {aiLoading && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-3" />
                      <p className="text-sm text-muted-foreground">Analisando incidente, histórico e propondo solução...</p>
                    </div>
                  )}

                  {aiResult && !aiLoading && (
                    <div className="space-y-4">
                      <Card className="border-purple-200 bg-purple-50/30">
                        <CardContent className="p-4">
                          <pre className="whitespace-pre-wrap text-sm font-sans">{aiResult}</pre>
                        </CardContent>
                      </Card>

                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => { setAiResult(null); }} className="gap-2">
                          <Sparkles className="h-4 w-4" />Nova Análise
                        </Button>
                        <Button onClick={async () => {
                          setSavingSolution(true);
                          try {
                            const title = `RCA: ${incident?.name?.substring(0, 80) || 'Incidente'} — ${details?.host?.name || incident?.host_name || ''}`;
                            await axios.post(`${API}/kb/solutions`, {
                              title,
                              content: aiResult,
                              category: details?.host?.category || 'Outro',
                              tags: [details?.host?.category, incident?.severity_label, 'RCA', 'AI'].filter(Boolean),
                              related_triggers: [incident?.name].filter(Boolean),
                            }, { headers: authHeader });
                            toast.success('Solução salva na Base de Conhecimento');
                          } catch (err) {
                            toast.error('Erro ao salvar solução');
                          } finally { setSavingSolution(false); }
                        }} disabled={savingSolution} className="gap-2">
                          {savingSolution ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
                          Salvar na Base de Conhecimento
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function IncidentBoardPage() {
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/incident-board`, { headers: getAuthHeader() });
      setColumns(res.data.columns || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar board');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleDragEnd = async (result) => {
    const { draggableId, source, destination } = result;
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

    const srcCol = source.droppableId;
    const dstCol = destination.droppableId;

    // Optimistic update
    const newColumns = { ...columns };
    const srcItems = [...(newColumns[srcCol] || [])];
    const dstItems = srcCol === dstCol ? srcItems : [...(newColumns[dstCol] || [])];
    const [moved] = srcItems.splice(source.index, 1);
    dstItems.splice(destination.index, 0, moved);
    newColumns[srcCol] = srcItems;
    if (srcCol !== dstCol) newColumns[dstCol] = dstItems;
    setColumns(newColumns);

    // Persist
    try {
      await axios.post(`${API}/incident-board/move`, {
        incident_id: draggableId,
        from_column: srcCol,
        to_column: dstCol,
      }, { headers: getAuthHeader() });
    } catch (err) {
      toast.error('Erro ao mover incidente');
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 p-2">
        {COLUMNS.map(c => (
          <div key={c.id} className="flex-1 min-w-[260px]">
            <Skeleton className="h-10 w-full mb-2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="incident-board">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-board-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Atualizar
        </Button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-5 gap-3">
          {COLUMNS.map(col => (
            <KanbanColumn
              key={col.id}
              column={col}
              incidents={columns[col.id] || []}
              onCardClick={setSelectedIncident}
            />
          ))}
        </div>
      </DragDropContext>

      <IncidentDetailModal
        incident={selectedIncident}
        open={!!selectedIncident}
        onClose={() => setSelectedIncident(null)}
        authHeader={getAuthHeader()}
      />
    </div>
  );
}
